import React, { ReactNode, useMemo } from 'react';
import { Modal as AntModal, ModalProps, Row } from 'antd';
import styled from 'styled-components';
import { CloseCircleOutlined } from '@ant-design/icons';
import { COLORS, FONT_WEIGHTS, TEXT_SIZES } from '../../utils/constants';

interface CommonModalProps extends ModalProps {
  title: string;
  open: boolean;
  onCancel: () => void;
  children: ReactNode;
  width?: number;
  height?: number;
  minHeight?: number | string;
  maxHeight?: number | string;
  footer?: ReactNode;
  footerAlign?: 'start' | 'end';
  className?: string;
  headerBgColor?: string;
  removeFooterMargin?: boolean;
}

const Modal: React.FC<CommonModalProps> = ({
  title,
  open,
  onCancel,
  children,
  width = 600,
  minHeight,
  maxHeight,
  footer = null,
  footerAlign = 'start',
  className,
  headerBgColor,
  removeFooterMargin = false,
  ...restProps
}) => {
  const CloseIcon = useMemo(() => <StyledCloseIcon />, []);

  return (
    <StyledModal
      title={title}
      open={open}
      onCancel={onCancel}
      footer={[
        <FooterRow key="footer" align={footerAlign}>
          {footer}
        </FooterRow>,
      ]}
      width={width}
      closeIcon={CloseIcon}
      className={className}
      $headerBgColor={headerBgColor}
      $minHeight={minHeight}
      $maxHeight={maxHeight}
      $removeFooterMargin={removeFooterMargin}
      {...restProps}
    >
      {children}
    </StyledModal>
  );
};

const StyledModal = styled(AntModal)<{
  $headerBgColor?: string;
  $minHeight?: number | string;
  $maxHeight?: number | string;
  $removeFooterMargin?: boolean;
}>`
  .ant-modal-header {
    margin-bottom: 0;
    padding: 16px 24px;
  }
  .ant-modal-title {
    font-weight: ${FONT_WEIGHTS.REGULAR};
  }
  .ant-modal-content {
    padding: 0;
    overflow: hidden;
  }
  .ant-modal-body {
    padding: 24px;
    min-height: ${(props) => props.$minHeight || '450px'};
    max-height: ${(props) => props.$maxHeight || '700px'};
    overflow-y: auto;
  }

  .ant-modal-footer {
    padding: 0px 24px;
    margin-bottom: ${(props) => (props.$removeFooterMargin ? '0px' : '32px')};
  }
`;

const StyledCloseIcon = styled(CloseCircleOutlined)`
  font-size: ${TEXT_SIZES.XXL}px;
  color: ${COLORS.white.primary};
`;

const FooterRow = styled(Row)<{ align?: 'start' | 'end' }>`
  width: 100%;
  justify-content: ${(props) =>
    props.align === 'start' ? 'flex-start' : 'flex-end'};
  gap: 16px;
`;

export default Modal;
